
(function () {
  if (customElements.get('notify-me-modal')) return;

  const KLAVIYO_BASE_URL = 'https://a.klaviyo.com';
  const KLAVIYO_HEADERS = {
    revision: '2026-07-15',
    'Content-Type': 'application/json',
  };
  const REQUEST_TIMEOUT_MS = 15000;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const NOTIFY_ME_SOURCE = 'Notify Me (PDP)';


  function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timeoutId = setTimeout(function () {
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    const fetchOptions = Object.assign({}, options, {signal: controller.signal});

    return fetch(url, fetchOptions).finally(function () {
      clearTimeout(timeoutId);
    });
  }

  class NotifyMeModal extends HTMLElement {
    connectedCallback() {

      if (!this._didMove) {
        this._didMove = true;
        document.body.appendChild(this);
        return;
      }
      this._init();
    }

    _init() {
      this.modal = this.querySelector('modal-content');
      this.form = this.querySelector('[data-notify-me-form]');
      if (!this.form) {
        console.error('notify-me-modal: [data-notify-me-form] not found — aborting setup.');
        return;
      }

      this.variantField = this.form.querySelector('[data-notify-me-variant]');
      this.emailField = this.form.querySelector('input[name="email"]');
      this.newsletterField = this.form.querySelector('input[name="newsletter"]');
      this.submitButton = this.form.querySelector('[data-notify-me-submit]');
      this.successBanner = this.form.querySelector('[data-notify-me-success]');
      this.errorBanner = this.form.querySelector('[data-notify-me-error]');
      this.errorMessage = this.errorBanner
        ? this.errorBanner.querySelector('[data-notify-me-error-message]')
        : null;
      this._defaultErrorText = this.errorMessage ? this.errorMessage.textContent : '';

      const productScript = this.querySelector('script[data-notify-me-product]');
      try {
        this.product = JSON.parse((productScript && productScript.textContent) || '');
      } catch (error) {
        console.error('notify-me-modal: could not parse [data-notify-me-product] JSON — aborting setup.', error);
        return;
      }

      this.productForm = this.dataset.productFormId
        ? document.getElementById(this.dataset.productFormId)
        : null;
      if (this.productForm) {
        this.productForm.addEventListener('variant:changed', this._onVariantChanged.bind(this));
      }

      if (this.modal) {
        this._modalObserver = new MutationObserver(this._onModalAttributeChanged.bind(this));
        this._modalObserver.observe(this.modal, {attributes: true, attributeFilter: ['open']});
      }

      this.form.addEventListener('submit', this._onSubmit.bind(this));
    }

    _onModalAttributeChanged(mutations) {
      for (let i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'open' && this.modal.hasAttribute('open')) {
          this._resetState();
          return;
        }
      }
    }

    _onVariantChanged(event) {
      const variant = event.detail && event.detail.variant;
      if (variant && variant.id != null) {
        this._setVariantValue(variant.id);
      }
    }

    _resetState() {
      if (this.errorBanner) this.errorBanner.hidden = true;
      if (this.successBanner) this.successBanner.hidden = true;
      if (this.submitButton) this.submitButton.hidden = false;
      if (this.errorMessage) this.errorMessage.textContent = this._defaultErrorText;
      this._setFieldsDisabled(false);
      this._syncVariantFromProductForm();
    }

    _syncVariantFromProductForm() {
      if (!this.productForm) return;
      const idField = this.productForm.querySelector('input[name="id"]');
      if (idField && idField.value) {
        this._setVariantValue(idField.value);
      }
    }

    _setVariantValue(variantId) {
      if (!this.variantField || variantId == null) return;
      const value = String(variantId);
      if (this.variantField.tagName === 'SELECT') {
        const hasOption = Array.prototype.some.call(this.variantField.options, function (option) {
          return option.value === value;
        });
        if (!hasOption) return;
      }
      this.variantField.value = value;
    }

    _setFieldsDisabled(disabled) {
      [this.emailField, this.newsletterField, this.variantField].forEach(function (field) {
        if (field) field.disabled = disabled;
      });
    }

    _showError(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message || this._defaultErrorText;
      }
      if (this.errorBanner) this.errorBanner.hidden = false;
      if (this.successBanner) this.successBanner.hidden = true;
    }

    _showSuccess() {
      if (this.successBanner) this.successBanner.hidden = false;
      if (this.errorBanner) this.errorBanner.hidden = true;
      if (this.submitButton) this.submitButton.hidden = true;
      this._setFieldsDisabled(true);
    }

    _findVariant(variantId) {
      if (!this.product || !Array.isArray(this.product.variants)) return null;
      const value = String(variantId);
      for (let i = 0; i < this.product.variants.length; i++) {
        if (String(this.product.variants[i].id) === value) return this.product.variants[i];
      }
      return null;
    }

    _extractErrorDetail(bodyText) {
      try {
        const parsed = JSON.parse(bodyText);
        if (parsed && Array.isArray(parsed.errors) && parsed.errors[0] && parsed.errors[0].detail) {
          return parsed.errors[0].detail;
        }
      } catch (error) {
        // Body wasn't JSON (or had no errors[0].detail) — caller falls back
        // to the default error text.
      }
      return null;
    }

    async _readErrorBody(name, response) {
      let bodyText = '';
      try {
        bodyText = await response.text();
      } catch (error) {
        console.error('notify-me-modal: could not read response body for ' + name, error);
      }
      console.error('notify-me-modal: ' + name + ' request failed with status ' + response.status, bodyText);
      return bodyText;
    }

    _subscribeToList(companyId, listId, email) {
      return fetchWithTimeout(
        KLAVIYO_BASE_URL + '/client/subscriptions/?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: KLAVIYO_HEADERS,
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: NOTIFY_ME_SOURCE,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email,
                      properties: {$source: NOTIFY_ME_SOURCE},
                      subscriptions: {
                        email: {marketing: {consent: 'SUBSCRIBED'}},
                      },
                    },
                  },
                },
              },
              relationships: {
                list: {data: {type: 'list', id: listId}},
              },
            },
          }),
        }
      );
    }

    _createBackInStockSubscription(companyId, email, variantId) {
      return fetchWithTimeout(
        KLAVIYO_BASE_URL + '/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: KLAVIYO_HEADERS,
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {data: {type: 'profile', attributes: {email: email}}},
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: '$shopify:::$default:::' + variantId,
                  },
                },
              },
            },
          }),
        }
      );
    }

    _logNotifyMeEvent(companyId, email, variantId) {
      const variant = this._findVariant(variantId);
      const product = this.product || {};
      return fetchWithTimeout(
        KLAVIYO_BASE_URL + '/client/events/?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: KLAVIYO_HEADERS,
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                metric: {
                  data: {type: 'metric', attributes: {name: 'Notify Me Request'}},
                },
                profile: {data: {type: 'profile', attributes: {email: email}}},
                properties: {
                  ProductID: product.id,
                  ProductName: product.title,
                  ProductHandle: product.handle,
                  ProductURL: product.url,
                  ImageURL: product.image,
                  Tags: product.tags,
                  VariantID: variantId,
                  VariantTitle: variant ? variant.title : null,
                  VariantSKU: variant ? variant.sku : null,
                  Source: NOTIFY_ME_SOURCE,
                },
              },
            },
          }),
        }
      );
    }

    async _onSubmit(event) {
      event.preventDefault();

      const email = this.emailField ? this.emailField.value.trim() : '';
      if (!this.emailField || !this.emailField.checkValidity() || !EMAIL_PATTERN.test(email)) {
        this._showError();
        if (this.emailField) this.emailField.focus();
        return;
      }

      const companyId = this.dataset.companyId;
      const listId = this.dataset.listId;
      const newsletterListId = this.dataset.newsletterListId;

      if (!companyId || !listId) {
        console.error('notify-me-modal: missing required data-company-id and/or data-list-id on <notify-me-modal>.');
        this._showError();
        return;
      }

      const variantId = this.variantField ? this.variantField.value : '';
      const wantsNewsletter = !!(this.newsletterField && this.newsletterField.checked);

      if (this.submitButton) {
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.disabled = true;
      }
      if (this.errorBanner) this.errorBanner.hidden = true;
      if (this.successBanner) this.successBanner.hidden = true;

      try {
        const tasks = [
          {name: 'client/subscriptions (back-in-stock list)', promise: this._subscribeToList(companyId, listId, email)},
          {name: 'client/back-in-stock-subscriptions', promise: this._createBackInStockSubscription(companyId, email, variantId)},
        ];
        if (wantsNewsletter && newsletterListId) {
          tasks.push({name: 'client/subscriptions (newsletter list)', promise: this._subscribeToList(companyId, newsletterListId, email)});
        }
        tasks.push({name: 'client/events', promise: this._logNotifyMeEvent(companyId, email, variantId)});

        const results = await Promise.allSettled(tasks.map(function (task) {
          return task.promise;
        }));

        let primarySucceeded = false;
        let primaryErrorDetail = null;

        for (let i = 0; i < results.length; i++) {
          const result = results[i];
          const name = tasks[i].name;

          if (result.status === 'rejected') {
            console.error('notify-me-modal: ' + name + ' request failed', result.reason);
            continue;
          }

          const response = result.value;
          if (response.ok) {
            if (i === 0) primarySucceeded = true;
            continue;
          }

          let bodyText = await this._readErrorBody(name, response);
          if (i === 0) {
            primaryErrorDetail = this._extractErrorDetail(bodyText);
          }
        }

        if (primarySucceeded) {
          // Klaviyo returns 202 with an empty body on success — do not
          // attempt to parse it as JSON.
          this._showSuccess();
        } else {
          this._showError(primaryErrorDetail);
        }
      } catch (error) {
        console.error('notify-me-modal: unexpected error while sending notify-me request', error);
        this._showError();
      } finally {
        if (this.submitButton) {
          this.submitButton.removeAttribute('aria-busy');
          this.submitButton.disabled = false;
        }
      }
    }
  }

  customElements.define('notify-me-modal', NotifyMeModal);
})();
